import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { CompanyInfoProvider } from "./contexts/CompanyInfoContext";
import ProtectedRoute from "./components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import RedirectHandler from "./components/RedirectHandler";
import Header from "./components/Header";
import Hero from "./components/Hero";
import ServicesPublic from "./components/ServicesPublic";
import ReviewsPublic from "./components/ReviewsPublic";
import ContactPublic from "./components/ContactPublic";
import Footer from "./components/Footer";
import BookingModal from "./components/BookingModal";
import Chatbot from "./components/Chatbot";
import AdminDashboard from "./components/AdminDashboard";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ForcePasswordChange from "./pages/ForcePasswordChange";
import DynamicPage from "./pages/DynamicPage";
import NewsArchive from "./pages/NewsArchive";
import CampaignArchive from "./pages/CampaignArchive";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ContactPage from "./pages/ContactPage";
import NotFound from "./pages/NotFound";
import AdminProfile from "./pages/AdminProfile";
import CookieConsent from "./components/CookieConsent";
import ServicesPage from "./pages/ServicesPage";
import BookingPage from "./pages/BookingPage";
import TeamPage from "./pages/TeamPage";
import ReviewsPage from "./pages/ReviewsPage";
import ReferencesPage from "./pages/ReferencesPage";
import SingleCasePage from "./pages/SingleCasePage";
import TrustBar from "./components/TrustBar";
import CustomerCases from "./components/CustomerCases";
import ScriptInjector from "./components/ScriptInjector";
import SEO from "./components/SEO";
import FeedbackWidget from "./components/FeedbackWidget";
import LatestNews from "./components/LatestNews";
import LatestCampaigns from "./components/LatestCampaigns";
import logger from "./utils/logger";
import { initGA, initScrollTracking, trackPageView } from "./utils/analytics";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";

const NyheterRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/aktuelt/${slug}`} replace />;
};

// Floating chrome (cookie banner, feedback widget) hidden on embeddable pages like /booking
const GlobalChrome = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/booking")) return null;
  return (
    <>
      <CookieConsent />
      <FeedbackWidget />
    </>
  );
};

const HomePage = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  useEffect(() => {
    // Log page view
    logger.pageView('Home');
  }, []);

  const handleBookingClick = () => {
    setIsBookingModalOpen(true);
    logger.userAction('booking_modal_opened');
  };

  const handleBookingClose = () => {
    setIsBookingModalOpen(false);
  };

  return (
    <>
      <SEO 
        title={null}
        description="Profesjonell EU-kontroll og service for tungbil, tilhenger og personbil. Moderne verksted i Sagstua med kapasitet for 6 store tilhengere samtidig."
        rating={{ value: 4.8, count: 127 }}
      />
      <Header onBookingClick={handleBookingClick} />
      <Hero onBookingClick={handleBookingClick} />
      <LatestCampaigns />
      <ServicesPublic />
      <CustomerCases limit={3} />
      <ReviewsPublic />
      <ContactPublic />
      <LatestNews />
      <TrustBar variant="full" />
      <Footer />
      <BookingModal isOpen={isBookingModalOpen} onClose={handleBookingClose} />
      <Chatbot />
    </>
  );
};

function App() {
  useEffect(() => {
    // Initialize Google Analytics (will get from CMS later)
    const gaId = 'G-XXXXXXXXXX'; // Placeholder - will be set via admin
    if (gaId && gaId !== 'G-XXXXXXXXXX') {
      initGA(gaId);
      initScrollTracking();
    }
  }, []);

  return (
    <HelmetProvider>
      <CompanyInfoProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <RedirectHandler />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/admin/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route 
                path="/force-password-change" 
                element={
                  <ProtectedRoute>
                    <ForcePasswordChange />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin/dashboard" 
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                } 
              />
              
              {/* Archive pages */}
              <Route path="/aktuelt" element={<NewsArchive />} />
              <Route path="/kampanjer" element={<CampaignArchive />} />
              
              {/* 301 redirects for old URLs */}
              <Route path="/nyheter" element={<Navigate to="/aktuelt" replace />} />
              <Route path="/nyheter/:slug" element={<NyheterRedirect />} />
              
              {/* Dedicated content pages */}
              <Route path="/tjenester" element={<ServicesPage />} />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/anmeldelser" element={<ReviewsPage />} />
              <Route path="/referanser" element={<ReferencesPage />} />
              <Route path="/referanse/:slug" element={<SingleCasePage />} />
              
              {/* Legal pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/personvern" element={<DynamicPage pageType="general" slugOverride="personvern" />} />
              <Route path="/vilkar" element={<DynamicPage pageType="general" slugOverride="vilkar" />} />
              <Route path="/cookies" element={<DynamicPage pageType="general" slugOverride="cookies" />} />
              
              {/* Admin profile */}
              <Route 
                path="/admin/profile" 
                element={
                  <ProtectedRoute>
                    <AdminProfile />
                  </ProtectedRoute>
                } 
              />
              
              {/* Contact Page - Dedicated page for NAP and contact form */}
              <Route path="/kontakt" element={<ContactPage />} />

              {/* Standalone booking page (iframe-embeddable, no header/footer) */}
              <Route path="/booking" element={<BookingPage />} />
              
              {/* Dynamic page routes */}
              <Route path="/tjeneste/:slug" element={<DynamicPage pageType="tjeneste" />} />
              <Route path="/aktuelt/:slug" element={<DynamicPage pageType="aktuelt" />} />
              <Route path="/kampanje/:slug" element={<DynamicPage pageType="kampanje" />} />
              
              {/* 404 Catch-all route - must be last */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <Toaster />
            <ScriptInjector />
            <GlobalChrome />
          </AuthProvider>
        </BrowserRouter>
      </div>
      </CompanyInfoProvider>
    </HelmetProvider>
  );
}

export default App;
